import React from 'react';
import { useTranslation } from 'react-i18next';
import { HorizontalFilterBar } from '../../Listings/Filters/HorizontalFilterBar';

interface Filters {
  cuisineTypes: string[];
  mealTypes: string[];
  dietaryPreferences: string[];
  timeRange: string;
}

interface RecipeFiltersProps {
  filters: Filters;
  onFilterChange: (filterType: keyof Filters, values: string[]) => void;
}

const cuisineTypes = [
  { value: 'italian', label: 'Italian' },
  { value: 'mexican', label: 'Mexican' },
  { value: 'indian', label: 'Indian' },
  { value: 'chinese', label: 'Chinese' },
  { value: 'japanese', label: 'Japanese' },
  { value: 'thai', label: 'Thai' },
  { value: 'mediterranean', label: 'Mediterranean' },
  { value: 'french', label: 'French' },
  { value: 'american', label: 'American' },
  { value: 'middle_eastern', label: 'Middle Eastern' }
];

const dietaryPreferences = [
  { value: 'vegetarian', label: 'Vegetarian' },
  { value: 'vegan', label: 'Vegan' },
  { value: 'gluten_free', label: 'Gluten Free' },
  { value: 'dairy_free', label: 'Dairy Free' },
  { value: 'keto', label: 'Keto' },
  { value: 'paleo', label: 'Paleo' },
  { value: 'low_carb', label: 'Low Carb' },
  { value: 'nut_free', label: 'Nut Free' }
];

const mealTypes = [
  { value: 'breakfast', labelKey: 'breakfast' },
  { value: 'lunch', labelKey: 'lunch' },
  { value: 'dinner', labelKey: 'dinner' },
  { value: 'snack', labelKey: 'snack' },
  { value: 'apero', labelKey: 'apero' },
  { value: 'drink', labelKey: 'drink' },
  { value: 'other', labelKey: 'other' }
];

const timeRanges = [
  { value: 'under_30', label: 'Under 30 mins' },
  { value: '30_60', label: '30-60 mins' },
  { value: 'over_60', label: 'Over 60 mins' }
];

export function RecipeFilters({ filters, onFilterChange }: RecipeFiltersProps) {
  const { t } = useTranslation();
  const hasActiveFilters = filters.cuisineTypes.length > 0 ||
                          filters.mealTypes.length > 0 ||
                          filters.dietaryPreferences.length > 0 ||
                          filters.timeRange !== '';

  const clearAllFilters = () => {
    onFilterChange('cuisineTypes', []);
    onFilterChange('mealTypes', []);
    onFilterChange('dietaryPreferences', []);
    onFilterChange('timeRange', ['']);
  };

  const filterGroups = [
    {
      label: 'Cuisine Type',
      options: cuisineTypes,
      selectedValues: filters.cuisineTypes,
      onChange: (values: string[]) => onFilterChange('cuisineTypes', values),
      multiSelect: true
    },
    {
      label: t('filters.mealType', 'Meal Type'),
      options: mealTypes.map(m => ({ value: m.value, label: t(`filters.mealTypeOptions.${m.labelKey}`) })),
      selectedValues: filters.mealTypes,
      onChange: (values: string[]) => onFilterChange('mealTypes', values),
      multiSelect: true
    },
    {
      label: 'Dietary Preferences',
      options: dietaryPreferences,
      selectedValues: filters.dietaryPreferences,
      onChange: (values: string[]) => onFilterChange('dietaryPreferences', values),
      multiSelect: true
    },
    {
      label: 'Time',
      options: timeRanges,
      selectedValues: filters.timeRange ? [filters.timeRange] : [],
      onChange: (values: string[]) => onFilterChange('timeRange', values),
      multiSelect: false
    }
  ];

  return (
    <HorizontalFilterBar
      filterGroups={filterGroups}
      onClearAll={clearAllFilters}
      hasActiveFilters={hasActiveFilters}
    />
  );
}